import type { MechanicDefinition } from "./types";

export const characterProgression: MechanicDefinition = {
  id: "character_progression",
  disabled: true,
  name: "Character Progression",
  description: "XP, leveling, stat growth, and ability unlocks (D&D/Talisman-style)",
  icon: "TrendingUp",
  category: "systems",
  aiHints: {
    purpose: "XP-based leveling with stat growth and ability unlocks — alternative to class-based progression.",
    auditPasses: 1,
    whenToUse: "Games wanting level progression without class restrictions. Good for classless or freeform systems.",
    synergies: ["skill_checks", "combat"],
    conflicts: ["character_classes"],
    prerequisites: [],
    tags: ["xp", "leveling", "progression", "stats", "growth"],
    complexity: 2,
    exampleConfig: { xp_per_level: 100, stat_growth: "choose_one" },
    stateVariables: ["player_level", "player_xp"],
  },
  configSchema: [
    {
      key: "xp_system",
      label: "XP System",
      type: "select",
      options: [
        { value: "milestone", label: "Milestone — level up at story beats", description: "No XP tracking. The narrator awards level-ups at major story completions. Best for narrative-focused campaigns where grinding isn't desired." },
        { value: "xp_track", label: "XP Track — earn XP from actions", description: "Classic XP system. Award XP for combat, quests, discoveries, and clever solutions. Uses an exponential curve. Most granular progression." },
        { value: "talisman", label: "Talisman-style — defeat enemies to gain stats", description: "Defeat enemies to collect trophies. Every 7 combat trophies = +1 STR. Every 7 magic trophies = +1 INT. No traditional levels — stats grow directly." },
      ],
      default: "xp_track",
    },
    {
      key: "max_level",
      label: "Max Level",
      type: "number",
      default: 20,
      description: "Maximum character level",
    },
    {
      key: "stat_growth",
      label: "Stat Growth",
      type: "select",
      options: [
        { value: "fixed", label: "Fixed — predefined stat gains per level", description: "Each level has predetermined stat increases based on class. Predictable and balanced." },
        { value: "choice", label: "Choice — player picks stats to improve", description: "On level up, the player CHOOSES which stats to increase. Ask them! Most player agency." },
        { value: "random", label: "Random — roll for stat gains", description: "Roll 1d4 for each stat increase on level up. Unpredictable but exciting." },
      ],
      default: "choice",
    },
    {
      key: "ability_unlocks",
      label: "Ability Unlocks",
      type: "boolean",
      default: true,
      description: "Every 3 levels, offer the player a choice of 2-3 new abilities/perks appropriate to their class and playstyle.",
    },
  ],
  systemPromptSection: (config) => {
    const xpSystem = config.xp_system || "xp_track";
    const maxLevel = config.max_level || 20;
    const statGrowth = config.stat_growth || "choice";
    const abilities = config.ability_unlocks !== false;

    const thresholds = Array.from({ length: 5 }, (_, i) => `Level ${i + 2}: ${(i + 1) * 100 * (i + 1)} XP`).join(", ");

    return `## CHARACTER PROGRESSION (Rules — Mandatory Post-Turn + Event-Reactive)
- System: ${xpSystem === "milestone" ? "Milestone-based. Award level-ups at major story completions." : xpSystem === "talisman" ? "Talisman-style: Defeat enemies for trophies. 7 trophies = +1 stat." : `XP-based. Award XP for combat, quests, discoveries, clever solutions.`}
- Max level: ${maxLevel}. XP curve (exponential): ${thresholds}.
- MANDATORY: The onEvent handler auto-accumulates XP from combat kills and quest completions into _xp_pending.
- MANDATORY: The postTurnHook applies _xp_pending to current_xp each turn and checks for level-up.
- When level-up occurs, the postTurnHook will prompt you to announce it and handle stat growth.
- Stat growth: ${statGrowth === "fixed" ? "Predefined increases per level." : statGrowth === "random" ? "Roll 1d4 per stat." : "Player CHOOSES which stat to increase. Ask them!"}
${abilities ? "- Every 3 levels, offer 2-3 new abilities appropriate to class and playstyle." : ""}
- HP increases on level up (class-dependent die + CON modifier).`;
  },
  widgets: [
    {
      id: "xp_bar",
      type: "status_bar_item",
      label: "Level",
      icon: "TrendingUp",
      component: "XPBarWidget",
      dataBindings: {
        level: "variables.character_level",
        currentXP: "variables.current_xp",
        xpToNext: "variables.xp_to_next_level",
      },
      visibility: "when_data",
      priority: 2,
    },
    {
      id: "progression_tab",
      type: "sidebar_tab",
      label: "Progression",
      icon: "TrendingUp",
      component: "ProgressionWidget",
      dataBindings: {
        level: "variables.character_level",
        currentXP: "variables.current_xp",
        xpToNext: "variables.xp_to_next_level",
        abilities: "variables.unlocked_abilities",
        trophyCombat: "variables.trophy_combat",
        trophyMagic: "variables.trophy_magic",
      },
      visibility: "when_data",
      priority: 20,
    },
  ],
  initializeState: (config) => {
    const base: Record<string, any> = {
      character_level: 1,
      current_xp: 0,
      xp_to_next_level: 100,
      unlocked_abilities: [],
    };
    if (config.xp_system === "talisman") {
      base.trophy_combat = 0;
      base.trophy_magic = 0;
    }
    return base;
  },
  onEvent: (event) => {
    if (event.type === "combat_kill") {
      const xp = event.data.xp_reward || 50;
      return {
        stateUpdates: { _xp_pending: xp },
        narrativeNotes: [`⭐ Combat victory! +${xp} XP pending.`],
      };
    }
    if (event.type === "quest_complete") {
      const xp = event.data.xp_reward || 150;
      return {
        stateUpdates: { _xp_pending: xp },
        narrativeNotes: [`⭐ Quest complete! +${xp} XP pending.`],
      };
    }
    return null;
  },
  postTurnHook: (context) => {
    const vars = context.state.variables || {};
    const pendingXP = vars._xp_pending || 0;
    if (pendingXP <= 0) return null;

    const currentXP = (vars.current_xp || 0) + pendingXP;
    const currentLevel = vars.character_level || 1;
    const xpToNext = vars.xp_to_next_level || (currentLevel * 100);
    const levelUp = currentXP >= xpToNext;
    const newLevel = levelUp ? currentLevel + 1 : currentLevel;
    const remainingXP = levelUp ? currentXP - xpToNext : currentXP;
    const newThreshold = newLevel * newLevel * 100;

    const updates: Record<string, any> = {
      current_xp: remainingXP,
      character_level: newLevel,
      xp_to_next_level: levelUp ? newThreshold : xpToNext,
      _xp_pending: 0,
    };
    const notes: string[] = [`⭐ Auto-XP: +${pendingXP}. ${remainingXP}/${levelUp ? newThreshold : xpToNext} XP.`];

    if (levelUp) {
      notes.push(`🎉 LEVEL UP! Now level ${newLevel}! Prompt player for stat increase.`);
      if (context.config.ability_unlocks !== false && newLevel % 3 === 0) {
        notes.push(`🌟 Ability unlock milestone (level ${newLevel})! Offer 2-3 new abilities.`);
      }
    }

    return { stateUpdates: updates, narrativeNotes: notes };
  },
  tools: [
    {
      type: "function",
      function: {
        name: "award_xp",
        description: "Award experience points to the player. Checks for level-up threshold.",
        parameters: {
          type: "object",
          properties: {
            amount: { type: "number", description: "XP amount to award" },
            reason: { type: "string", description: "What earned this XP" },
            trophy_type: { type: "string", description: "If Talisman mode: 'combat' or 'magic' trophy type" },
          },
          required: ["amount", "reason"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "level_up",
        description: "Level up the character. Apply stat increases, HP growth, and ability unlocks.",
        parameters: {
          type: "object",
          properties: {
            new_level: { type: "number", description: "The new character level" },
            stat_increases: { type: "object", description: "Map of stat name to increase amount" },
            hp_increase: { type: "number", description: "HP gained this level" },
            new_ability: { type: "string", description: "New ability/perk unlocked" },
            new_xp_threshold: { type: "number", description: "XP needed for next level" },
          },
          required: ["new_level", "new_xp_threshold"],
        },
      },
    },
  ],
};
